import { customElement, state } from "lit/decorators.js";
import { Page } from "../components/component";
import { Task } from "@lit/task";
import { html, nothing } from "lit";
import { consume } from "@lit/context";
import { RouterContext, routerContext } from "../context/router.context";
import { type Ref, createRef, ref } from "lit/directives/ref.js";
import type { ModalNav } from "../modals/modal-nav";
import type { ModalSheetNav } from "../modals/modal-sheet-nav";
import { authContext, type AuthContext } from "../context/auth.context";
import { choose } from "lit/directives/choose.js";
import {
  Attachment,
  NumericBox,
  ResultInteger,
  ResultPercentage,
  Scale,
  TextBox,
  Url,
  YesOrNo,
} from "../components/evaluation";
import { actionSheetController, alertController, toastController } from "@ionic/core";
import { reportsStore } from "../stores/reports.store";
import {
  EvaluationFieldType,
  type EvaluationField,
  type EvaluationFieldAttachment,
  type EvaluationFieldNumericBox,
  type EvaluationFieldResultInteger,
  type EvaluationFieldResultPercentage,
  type EvaluationFieldScale,
  type EvaluationFieldTextBox,
  type EvaluationFieldUrl,
  type EvaluationFieldYesOrNo,
} from "../repository/report/report";
import { when } from "lit/directives/when.js";
import type { ObservableEvaluation, ObservableEvaluationSection } from "../stores/evaluation.store";
import type { IonRefresher } from "@ionic/core/components/ion-refresher";
import { localized, msg, str } from "@lit/localize";
import { ApiError } from "../error";

interface ProcessedContent {
  src: string;
  type: string | undefined;
  ratio?: number;
}

@customElement("page-evaluation")
@localized()
export class PageEvaluation extends Page {
  @state() private _reportId: string = "";
  @state() private _evaluationId: string = "";
  @state() processedElements: Array<ProcessedContent> = [];

  @consume({ context: routerContext }) router!: RouterContext;
  @consume({ context: authContext }) auth!: AuthContext;

  #ionRefresher: Ref<IonRefresher> = createRef();
  #modalNewSectionRef: Ref<ModalSheetNav> = createRef();
  #modalEditSectionRef: Ref<ModalSheetNav> = createRef();
  #modalEditReportRef: Ref<ModalNav> = createRef();
  #modalPickFieldRef: Ref<ModalSheetNav> = createRef();
  #modalEditAttachmentRef: Ref<ModalSheetNav> = createRef();
  #modalEditNumericBoxRef: Ref<ModalSheetNav> = createRef();
  #modalEditTextBoxRef: Ref<ModalSheetNav> = createRef();
  #modalEditResultIntegerRef: Ref<ModalSheetNav> = createRef();
  #modalEditResultPercentageRef: Ref<ModalSheetNav> = createRef();
  #modalEditUrlRef: Ref<ModalSheetNav> = createRef();
  #modalEditYesOrNoRef: Ref<ModalSheetNav> = createRef();

  connectedCallback() {
    super.connectedCallback();
    this._reportId = this.router.getParam("reportId");
    this._evaluationId = this.router.getParam("evaluationId");
    this.#evaluation.run([true]);
  }

  #evaluation = new Task(this, {
    task: async ([cache]: [boolean]) => {
      return reportsStore.loadEvaluation(this._reportId, this._evaluationId, { cache });
    },
  });

  async presentNewSectionBottomSheet(evaluation: ObservableEvaluation) {
    await this.#modalNewSectionRef.value?.open({ evaluation: evaluation });
  }

  async presentEditSectionBottomSheet(section: ObservableEvaluationSection) {
    await this.#modalEditSectionRef.value?.open({ section });
  }

  async presentEditReportModal(report: any) {
    await this.#modalEditReportRef.value?.open({ evaluation: report });
  }

  async presentPickFieldBottomSheet(section: ObservableEvaluationSection) {
    await this.#modalPickFieldRef.value?.open({ section: section });
  }

  async deleteEvaluation() {
    const alert = await alertController.create({
      header: msg("Apagar Avaliação"),
      message: msg("Tem a certeza que deseja apagar esta avaliação? A ação não é reversível."),
      buttons: [
        {
          text: msg("Cancelar"),
          role: "cancel",
        },
        {
          text: msg("Apagar"),
          role: "destructive",
          handler: async () => {
            try {
              await reportsStore.deleteEvaluation(this.#evaluation.value!);
              this.router.back();
            } catch (error) {
              if (error instanceof ApiError) {
                const alert = await alertController.create({
                  header: msg("Erro"),
                  message: error.message,
                  buttons: ["OK"],
                });
                await alert.present();
              }
            }
          },
        },
      ],
    });
    await alert.present();
  }

  async deleteSection(section: ObservableEvaluationSection) {
    const alert = await alertController.create({
      header: msg("Apagar Secção"),
      message: msg("Tem a certeza que deseja apagar esta secção? A ação não é reversível."),
      buttons: [
        {
          text: msg("Cancelar"),
          role: "cancel",
        },
        {
          text: msg("Apagar"),
          role: "destructive",
          handler: async () => {
            try {
              await reportsStore.deleteEvaluationSection(this.#evaluation.value!, section).then(async () => {
                const toast = await toastController.create({
                  message: msg("Secção apagada com sucesso"),
                  color: "primary",
                  icon: "/assets/icons/info.svg",
                  duration: 2000,
                });
                await toast.present();
              });
            } catch (error) {
              if (error instanceof ApiError) {
                const alert = await alertController.create({
                  header: msg("Erro"),
                  message: error.message,
                  buttons: ["OK"],
                });
                await alert.present();
              }
            }
          },
        },
      ],
    });
    await alert.present();
  }

  canEdit() {
    const report = reportsStore.findReport(this._reportId);
    if (!report) return false;
    return report.canEdit(this.auth.user);
  }

  async moveSectionUp(evaluation: ObservableEvaluation, section: ObservableEvaluationSection) {
    try {
      const previousSection = evaluation.sections.find((s) => s.position === section.position - 1);
      if (previousSection) {
        await reportsStore.updateEvaluationSectionPosition(
          section,
          { position: section.position - 1 },
          previousSection,
          {
            position: previousSection.position + 1,
          },
        );
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const alert = await alertController.create({
          header: msg("Erro"),
          message: error.message,
          buttons: ["OK"],
        });
        await alert.present();
      }
    }
  }

  async moveSectionDown(evaluation: ObservableEvaluation, section: ObservableEvaluationSection) {
    try {
      const nextSection = evaluation.sections.find((s) => s.position === section.position + 1);
      if (nextSection) {
        await reportsStore.updateEvaluationSectionPosition(section, { position: section.position + 1 }, nextSection, {
          position: nextSection.position - 1,
        });
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const alert = await alertController.create({
          header: msg("Erro"),
          message: error.message,
          buttons: ["OK"],
        });
        await alert.present();
      }
    }
  }

  async presentActionSheet(field: EvaluationField, section: ObservableEvaluationSection) {
    if (field.collectionName === EvaluationFieldType.Scale) {
      const actionSheet = await actionSheetController.create({
        header: msg("Ações"),
        buttons: [
          {
            text: msg("Remover"),
            role: "destructive",
            handler: async () => {
              await this.showDeleteFieldAlert(field, section);
            },
          },
          {
            text: msg("Cancelar"),
            role: "cancel",
          },
        ],
      });
      await actionSheet.present();
    } else {
      const actionSheet = await actionSheetController.create({
        header: msg("Ações"),
        buttons: [
          {
            text: msg("Editar"),
            handler: () => {
              this.openEditFieldModal(field, section);
            },
          },
          {
            text: msg("Remover"),
            role: "destructive",
            handler: async () => {
              await this.showDeleteFieldAlert(field, section);
            },
          },
          {
            text: msg("Cancelar"),
            role: "cancel",
          },
        ],
      });
      await actionSheet.present();
    }
  }

  async showDeleteFieldAlert(field: EvaluationField, section: ObservableEvaluationSection) {
    const alert = await alertController.create({
      header: msg("Apagar Campo"),
      message: msg("Tem a certeza que deseja apagar esta campo? A ação não é reversível."),
      buttons: [
        {
          text: msg("Cancelar"),
          role: "cancel",
        },
        {
          text: msg("Apagar"),
          role: "destructive",
          handler: async () => {
            try {
              await reportsStore.deleteEvaluationField(field, section);
            } catch (error) {
              if (error instanceof ApiError) {
                const alert = await alertController.create({
                  header: msg("Erro"),
                  message: error.message,
                  buttons: ["OK"],
                });
                await alert.present();
              }
            }
          },
        },
      ],
    });
    await alert.present();
  }

  openEditFieldModal(field: EvaluationField, section: ObservableEvaluationSection) {
    switch (field.collectionName) {
      case EvaluationFieldType.Attachment:
        this.#modalEditAttachmentRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.NumericBox:
        this.#modalEditNumericBoxRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.TextBox:
        this.#modalEditTextBoxRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.ResultInteger:
        this.#modalEditResultIntegerRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.ResultPercentage:
        this.#modalEditResultPercentageRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.Url:
        this.#modalEditUrlRef.value?.open({ field, section });
        break;
      case EvaluationFieldType.YesOrNo:
        this.#modalEditYesOrNoRef.value?.open({ field, section });
        break;
    }
  }

  render() {
    return html`
      <ion-header>
        <ion-toolbar class=${this.#evaluation.value?.sections.length === 0 ? "no-border" : ""}>
          <!-- remove the class above 'no-border' when there are sections -->
          <ion-buttons slot="start">
            <div class="flex items-center">
              <ion-router-link href=${this.router.backUrl} router-direction="back">
                <ion-button
                  style="--padding-inline-start: 0px; --padding-start: 0px; margin-inline-start: 0px; margin-start: 0px;"
                  fill="clear"
                  class="font-semibold">
                  <span class="flex items-center -ml-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M14 16L10 12L14 8"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                  ${msg("voltar")}
                </ion-button>
              </ion-router-link>
            </div>
          </ion-buttons>
          <ion-title>${this.#evaluation.value?.name}</ion-title>
          ${when(
            this.canEdit(),
            () => html`
              <ion-buttons slot="end">
                <div class="flex items-center">
                  <ion-button
                    id="action-popover"
                    fill="solid"
                    style="--padding-start: 10px; --padding-end: 10px; --padding-top: 6px; --padding-bottom: 6px;"
                    color="secondary"
                    shape="round"
                    size="xsmall">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      class="mr-1 mb-0.5">
                      <path
                        d="M11.6667 12.4998H17.5M2.5 12.4998H4.16667M4.16667 12.4998C4.16667 13.6504 5.09941 14.5832 6.25 14.5832C7.40059 14.5832 8.33333 13.6504 8.33333 12.4998C8.33333 11.3492 7.40059 10.4165 6.25 10.4165C5.09941 10.4165 4.16667 11.3492 4.16667 12.4998ZM16.6667 7.49984H17.5M2.5 7.49984H8.33333M13.75 9.58317C12.5994 9.58317 11.6667 8.65043 11.6667 7.49984C11.6667 6.34924 12.5994 5.4165 13.75 5.4165C14.9006 5.4165 15.8333 6.34924 15.8333 7.49984C15.8333 8.65043 14.9006 9.58317 13.75 9.58317Z"
                        stroke="#111111"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                    ${msg("Ações")}
                  </ion-button>
                </div>
              </ion-buttons>
              <ion-popover
                arrow
                alignment="center"
                dismiss-on-select="true"
                reference="trigger"
                side="bottom"
                style="--max-width: 150px; --offset-x: -8px; --backdrop-opacity: 0.3;"
                trigger="action-popover">
                <div class="p-0">
                  <ion-list>
                    <ion-item
                      button
                      .detail=${false}
                      class="no-p popover text-sm"
                      @click=${() => this.presentEditReportModal(this.#evaluation.value)}>
                      <ion-label class="font-semibold pl-3">${msg("Editar")}</ion-label>
                    </ion-item>
                    <ion-item button class="no-p no-detail text-sm" @click=${() => this.deleteEvaluation()}>
                      <ion-label class="font-semibold pl-3">${msg("Apagar")}</ion-label>
                    </ion-item>
                  </ion-list>
                </div>
              </ion-popover>
            `,
          )}
        </ion-toolbar>
      </ion-header>
      <ion-content fullscreen class="no-px" style="--padding-bottom: 24px;">
        <ion-refresher
          slot="fixed"
          @ionRefresh=${async () => {
            if (!this.auth.user) return;
            await this.#evaluation.run([false]);
            await this.#ionRefresher.value?.complete();
          }}
          ${ref(this.#ionRefresher)}>
          <ion-refresher-content class="mt-4" pulling-text=${msg("Puxe para atualizar")}></ion-refresher-content>
        </ion-refresher>

        ${this.#evaluation.render({
          pending: () => {},
          error: (error) => {
            console.log(error);
            return html`<span class="text-danger">${msg("Erro ao carregar a avaliação")}</span>`;
          },
          complete: (evaluation) => {
            return html`
              <ion-accordion-group class="mt-2.5 space-y-2.5" multiple>
                ${evaluation.sortedSections.map(
                  (section, index) => html`
                    <ion-accordion
                      class="px-4"
                      value="${section.id}"
                      toggle-icon-slot="start"
                      toggle-icon="/assets/icons/chevron-down.svg">
                      <ion-item lines="none" class="no-active-bg no-border no-p-start no-inner-p" slot="header">
                        <div class="flex justify-between items-center w-full">
                          <div class="flex items-center space-x-1 w-full">
                            <h6>${section.label}</h6>
                            <ion-chip style="--background: #EAEAEA; --color: #888888"
                              >${msg(str`${section.fields.length || 0} Campos`)}</ion-chip
                            >
                          </div>
                          ${when(
                            this.canEdit(),
                            () => html`
                              <ion-button
                                id="${index}-section-popover"
                                fill="clear"
                                size="chip"
                                @click="${(e: Event) => {
                                  e.stopPropagation();
                                }}">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none">
                                  <path
                                    d="M17 12C17 12.5523 17.4477 13 18 13C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11C17.4477 11 17 11.4477 17 12Z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round" />
                                  <path
                                    d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round" />
                                  <path
                                    d="M5 12C5 12.5523 5.44772 13 6 13C6.55228 13 7 12.5523 7 12C7 11.4477 6.55228 11 6 11C5.44772 11 5 11.4477 5 12Z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round" />
                                </svg>
                              </ion-button>
                              <ion-popover
                                arrow
                                alignment="center"
                                dismiss-on-select="true"
                                reference="trigger"
                                side="bottom"
                                style="--max-width: 150px; --offset-x: -8px; --backdrop-opacity: 0.3;"
                                trigger="${index}-section-popover">
                                <div class="p-0">
                                  <ion-list>
                                    <ion-item
                                      button
                                      .detail=${false}
                                      class="no-p popover text-sm"
                                      @click=${() => this.presentEditSectionBottomSheet(section)}>
                                      <ion-label class="font-semibold pl-3">${msg("Editar")}</ion-label>
                                    </ion-item>
                                    ${when(
                                      index !== 0,
                                      () => html`
                                        <ion-item
                                          button
                                          .detail=${false}
                                          class="no-p popover text-sm"
                                          @click=${() => this.moveSectionUp(evaluation, section)}>
                                          <ion-label class="font-semibold pl-3">${msg("Mover para Cima")}</ion-label>
                                        </ion-item>
                                      `,
                                    )}
                                    ${when(
                                      index !== evaluation.sections.length - 1,
                                      () => html`
                                        <ion-item
                                          button
                                          .detail=${false}
                                          class="no-p popover text-sm"
                                          @click=${() => this.moveSectionDown(evaluation, section)}>
                                          <ion-label class="font-semibold pl-3">${msg("Mover para Baixo")}</ion-label>
                                        </ion-item>
                                      `,
                                    )}
                                    <ion-item
                                      button
                                      class="no-p no-detail text-sm"
                                      @click=${() => this.deleteSection(section)}>
                                      <ion-label class="font-semibold pl-3">${msg("Apagar Secção")}</ion-label>
                                    </ion-item>
                                  </ion-list>
                                </div>
                              </ion-popover>
                            `,
                          )}
                        </div>
                      </ion-item>
                      ${when(
                        this.canEdit(),
                        () => html`
                          <div slot="content" class="my-1.5 space-y-3">
                            ${section.fields.map((field) =>
                              choose(field.collectionName, [
                                [
                                  EvaluationFieldType.Attachment,
                                  () =>
                                    Attachment(field as EvaluationFieldAttachment, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.NumericBox,
                                  () =>
                                    NumericBox(field as EvaluationFieldNumericBox, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.ResultInteger,
                                  () =>
                                    ResultInteger(field as EvaluationFieldResultInteger, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.ResultPercentage,
                                  () =>
                                    ResultPercentage(field as EvaluationFieldResultPercentage, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.Scale,
                                  () =>
                                    Scale(field as EvaluationFieldScale, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.TextBox,
                                  () =>
                                    TextBox(field as EvaluationFieldTextBox, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.Url,
                                  () =>
                                    Url(field as EvaluationFieldUrl, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                                [
                                  EvaluationFieldType.YesOrNo,
                                  () =>
                                    YesOrNo(field as EvaluationFieldYesOrNo, {
                                      readonly: true,
                                      onClick: () => {
                                        // this.#modalChooseEvaluationActions.value?.open({
                                        //   field: field,
                                        //   section: section,
                                        // });
                                        this.presentActionSheet(field, section);
                                      },
                                    }),
                                ],
                              ]),
                            )}
                            <ion-button
                              color="secondary"
                              expand="block"
                              shape="round"
                              size="small"
                              @click=${() => this.presentPickFieldBottomSheet(section)}
                              >${msg("Adicionar Campo")}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                class="ml-1.5">
                                <path
                                  d="M4 8H8M8 8H12M8 8V12M8 8V4"
                                  stroke="black"
                                  stroke-width="1.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round" />
                              </svg>
                            </ion-button>
                          </div>
                        `,
                        () => html`
                          <div slot="content" class="my-1.5 space-y-3">
                            ${section.fields.map((field) =>
                              choose(field.collectionName, [
                                [
                                  EvaluationFieldType.Attachment,
                                  () =>
                                    Attachment(field as EvaluationFieldAttachment, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.NumericBox,
                                  () =>
                                    NumericBox(field as EvaluationFieldNumericBox, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.ResultInteger,
                                  () =>
                                    ResultInteger(field as EvaluationFieldResultInteger, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.ResultPercentage,
                                  () =>
                                    ResultPercentage(field as EvaluationFieldResultPercentage, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.Scale,
                                  () =>
                                    Scale(field as EvaluationFieldScale, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.TextBox,
                                  () =>
                                    TextBox(field as EvaluationFieldTextBox, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.Url,
                                  () =>
                                    Url(field as EvaluationFieldUrl, {
                                      readonly: true,
                                    }),
                                ],
                                [
                                  EvaluationFieldType.YesOrNo,
                                  () =>
                                    YesOrNo(field as EvaluationFieldYesOrNo, {
                                      readonly: true,
                                    }),
                                ],
                              ]),
                            )}
                          </div>
                        `,
                      )}
                    </ion-accordion>
                    <div class="h-px w-full bg-[#e5e5e5] last:hidden"></div>
                  `,
                )}
              </ion-accordion-group>
              ${when(
                this.canEdit(),
                () => html`
                  <div class="relative w-full flex mt-2">
                    <ion-button
                      @click=${() => this.presentNewSectionBottomSheet(evaluation)}
                      size="chip"
                      shape="round"
                      class="mx-auto z-10">
                      ${msg("Criar Secção")}
                      <span class="flex items-center ml-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M4 8H8M8 8H12M8 8V12M8 8V4"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round" />
                        </svg>
                      </span>
                    </ion-button>
                    <div class="h-px w-full absolute inset-y-center bg-[#e5e5e5]"></div>
                  </div>
                `,
                () => {
                  if (this.#evaluation.value?.sections.length === 0) {
                    return html`
                      <div
                        class="mx-4 h-[139px] bg-accent-7 rounded-md col justify-center items-center space-y-1 text-accent-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 11V16M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21ZM12.0498 8V8.1L11.9502 8.1002V8H12.0498Z"
                            stroke="#111111"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round" />
                        </svg>
                        <span class="font-display font-semibold text-center" style="max-inline-size: 20ch">
                          ${msg("Esta avaliação ainda não tem conteúdo")}
                        </span>
                      </div>
                    `;
                  }
                  return nothing;
                },
              )}
            `;
          },
        })}
      </ion-content>

      <modal-nav root="modal-edit-report" ${ref(this.#modalEditReportRef)}></modal-nav>
      <modal-sheet-nav
        auto-height
        root="modal-create-evaluation-section"
        ${ref(this.#modalNewSectionRef)}></modal-sheet-nav>
      <modal-sheet-nav
        auto-height
        root="modal-edit-evaluation-section"
        ${ref(this.#modalEditSectionRef)}></modal-sheet-nav>
      <modal-sheet-nav
        initial-breakpoint=${1}
        .breakpoints=${[0, 1]}
        root="modal-choose-evaluation-field"
        ${ref(this.#modalPickFieldRef)}></modal-sheet-nav>
      <modal-sheet-nav auto-height root="modal-edit-attachment" ${ref(this.#modalEditAttachmentRef)}></modal-sheet-nav>
      <modal-sheet-nav auto-height root="modal-edit-numeric-box" ${ref(this.#modalEditNumericBoxRef)}></modal-sheet-nav>
      <modal-sheet-nav auto-height root="modal-edit-text-box" ${ref(this.#modalEditTextBoxRef)}></modal-sheet-nav>
      <modal-sheet-nav
        auto-height
        root="modal-edit-result-integer"
        ${ref(this.#modalEditResultIntegerRef)}></modal-sheet-nav>
      <modal-sheet-nav
        auto-height
        root="modal-edit-result-percentage"
        ${ref(this.#modalEditResultPercentageRef)}></modal-sheet-nav>
      <modal-sheet-nav auto-height root="modal-edit-url" ${ref(this.#modalEditUrlRef)}></modal-sheet-nav>
      <modal-sheet-nav
        auto-height
        root="modal-edit-yes-no-questionnaire"
        ${ref(this.#modalEditYesOrNoRef)}></modal-sheet-nav>
    `;
  }
}
